import { useEffect, useState } from 'react'
import {
    Box,
    Paper,
    Typography,
    CircularProgress,
    Alert,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Chip,
    Divider,
    Pagination,
    Stack,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { createTheme, ThemeProvider } from '@mui/material/styles'
import { getTripsForBoat, getTripGPSData, getTripTelemetryData, getBoatDTCs, getBoatAlarms } from '../../../../../apiRoutes'
import { useToastMessage } from '../../../../../context/ToastPopUpContext'
import TripMap from './TripMap'

const theme = createTheme({
    palette: {
        mode: 'dark',
        primary: { main: '#8cc63f' },
        secondary: { main: '#4caf50' },
        background: { default: '#1a1a1a', paper: '#2d2d2d' },
        text: { primary: '#ffffff', secondary: '#b0b0b0' },
    },
})

type Trip = {
    deviceId: string
    tripStart: number
    tripEnd: number
    duration_seconds: number
}

type GPSPoint = {
    timestamp: string
    latitude: number
    longitude: number
}

type TripsViewProps = {
    row: { deviceSerial?: string; device?: string }
}

const TripsView = ({ row }: TripsViewProps) => {
    const setToastMessage = useToastMessage()
    const [trips, setTrips] = useState<Trip[]>([])
    const [loading, setLoading] = useState(false)
    const [expandedTrip, setExpandedTrip] = useState<string | false>(false)
    const [tripGPSData, setTripGPSData] = useState<{ [key: string]: GPSPoint[] }>({})
    const [tripTelemetryData, setTripTelemetryData] = useState<{ [key: string]: any }>({})
    const [tripLocations, setTripLocations] = useState<{ [key: string]: string }>({})
    const [tripAlerts, setTripAlerts] = useState<{ [key: string]: { dtcs: number; alarms: number } }>({})
    const [loadingGPS, setLoadingGPS] = useState<string | null>(null)
    const [loadingTelemetry, setLoadingTelemetry] = useState<{ [key: string]: boolean } | null>(null)
    const [currentPage, setCurrentPage] = useState(1)
    const [tripsPerPage] = useState(5) // Fixed at 5 trips per page

    const deviceId = row?.deviceSerial || row?.device

    // Pagination logic
    const validTrips = trips.filter(trip => trip.tripEnd > trip.tripStart)
    const totalPages = Math.ceil(validTrips.length / tripsPerPage)
    const startIndex = (currentPage - 1) * tripsPerPage
    const endIndex = startIndex + tripsPerPage
    const currentTrips = validTrips.slice(startIndex, endIndex)

    const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
        setCurrentPage(value)
        setExpandedTrip(false) // Close any expanded trip when changing pages
    }

    // Format duration in a human-readable way
    const formatDuration = (seconds: number | string) => {
        // Handle negative durations (tripEnd before tripStart)
        const secondsNum = typeof seconds === 'string' ? parseFloat(seconds) : seconds
        const absSeconds = Math.abs(secondsNum)
        const hours = Math.floor(absSeconds / 3600)
        const minutes = Math.floor((absSeconds % 3600) / 60)

        if (hours > 0) {
            return `${hours} hr${hours > 1 ? 's' : ''} ${minutes} min`
        }
        return `${minutes} min`
    }

    // Format date and time
    const formatDateTime = (timestamp: number | string) => {
        // These timestamps are in milliseconds and represent future dates (test data)
        // Convert to number if it's a string
        const timestampNum = typeof timestamp === 'string' ? parseInt(timestamp) : timestamp
        const date = new Date(timestampNum)

        // Check if date is valid
        if (isNaN(date.getTime())) {
            return 'Invalid Date'
        }

        const dayName = date.toLocaleDateString('en-US', { weekday: 'long' })
        const monthDay = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
        const time = date.toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
        })

        return `${dayName}, ${monthDay} ${time}`
    }

    // Format date and time components for better visual display
    const formatTripComponents = (startTimestamp: number | string, endTimestamp: number | string) => {
        const startNum = typeof startTimestamp === 'string' ? parseInt(startTimestamp) : startTimestamp
        const endNum = typeof endTimestamp === 'string' ? parseInt(endTimestamp) : endTimestamp
        const startDate = new Date(startNum)
        const endDate = new Date(endNum)

        if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
            return { date: 'Invalid', startTime: 'Invalid', endTime: 'Invalid' }
        }

        // Format: "Wed Oct 2"
        const dayOfWeek = startDate.toLocaleDateString('en-US', { weekday: 'short' })
        const month = startDate.toLocaleDateString('en-US', { month: 'short' })
        const day = startDate.getDate()
        const date = `${dayOfWeek} ${month} ${day}`
        
        // Format times: "8:13AM"
        const startTime = startDate.toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
        })
        const endTime = endDate.toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
        })

        return { date, startTime, endTime }
    }

    // Get location from GPS coordinates (geocoding)
    const getLocationFromCoords = async (lat: number, lon: number) => {
        try {
            // Using a simple reverse geocoding service
            const response = await fetch(`https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${lat}&longitude=${lon}&localityLanguage=en`)
            const data = await response.json()
            
            if (data.city && data.principalSubdivision) {
                return `${data.city}, ${data.principalSubdivision}`
            } else if (data.locality && data.principalSubdivision) {
                return `${data.locality}, ${data.principalSubdivision}`
            } else if (data.principalSubdivision) {
                return data.principalSubdivision
            }
            return 'Unknown Location'
        } catch (error) {
            console.error('Geocoding error:', error)
            return 'Unknown Location'
        }
    }

    // Fetch trips for the device
    const fetchTrips = async () => {
        if (!deviceId) {
            setToastMessage('No device paired with this boat.', 'info')
            return
        }

        setLoading(true)
        try {
            const response = await getTripsForBoat({ deviceSerial: deviceId, limit: 50 })

            if (Array.isArray(response)) {
                if (response.length === 0) {
                    setToastMessage('No trips found for this device.', 'info')
                } else {
                    setTrips(response)
                    setCurrentPage(1) // Reset to first page when new data is loaded
                }
            } else {
                setToastMessage('No trips available for this device.', 'info')
            }
        } catch (error: any) {
            setToastMessage(error.message || 'Failed to fetch trips.', 'error')
            console.error('Error fetching trips:', error)
        } finally {
            setLoading(false)
        }
    }

    // Fetch GPS data for a specific trip
    const fetchTripGPSData = async (trip: Trip) => {
        const tripKey = `${trip.tripStart}-${trip.tripEnd}`

        if (tripGPSData[tripKey]) {
            return // Already loaded
        }

        setLoadingGPS(tripKey)
        try {
            const response = await getTripGPSData({
                deviceSerial: deviceId,
                tripStart: trip.tripStart,
                tripEnd: trip.tripEnd
            })
            if (Array.isArray(response)) {
                setTripGPSData(prev => ({
                    ...prev,
                    [tripKey]: response
                }))
            }
        } catch (error: any) {
            setToastMessage(error.message || 'Failed to fetch GPS data for this trip.', 'error')
            console.error('Error fetching GPS data:', error)
        } finally {
            setLoadingGPS(null)
        }
    }

    // Fetch telemetry data for a specific trip
    const fetchTripTelemetryData = async (trip: Trip) => {
        const tripKey = `${trip.tripStart}-${trip.tripEnd}`

        if (tripTelemetryData[tripKey]) {
            return // Already loaded
        }

        setLoadingTelemetry(prev => ({ ...prev, [tripKey]: true }))
        try {
            const response = await getTripTelemetryData({
                deviceSerial: deviceId,
                tripStart: trip.tripStart,
                tripEnd: trip.tripEnd
            })
            if (response && typeof response === 'object') {
                setTripTelemetryData(prev => ({
                    ...prev,
                    [tripKey]: response
                }))
            }
        } catch (error: any) {
            setToastMessage(error.message || 'Failed to fetch telemetry data for this trip.', 'error')
            console.error('Error fetching telemetry data:', error)
        } finally {
            setLoadingTelemetry(prev => {
                if (prev === null) return null
                const newState = { ...prev }
                delete newState[tripKey]
                return Object.keys(newState).length > 0 ? newState : null
            })
        }
    }

    // Fetch location data for a trip
    const fetchTripLocation = async (trip: Trip, gpsData: GPSPoint[]) => {
        const tripKey = `${trip.tripStart}-${trip.tripEnd}`
        
        if (tripLocations[tripKey] || gpsData.length === 0) {
            return // Already loaded or no GPS data
        }

        try {
            // Use the first GPS point for location
            const firstPoint = gpsData[0]
            const location = await getLocationFromCoords(
                parseFloat(String(firstPoint.latitude)),
                parseFloat(String(firstPoint.longitude))
            )
            
            setTripLocations(prev => ({
                ...prev,
                [tripKey]: location
            }))
        } catch (error) {
            console.error('Error fetching location:', error)
        }
    }

    // Fetch DTC and alarm data for a trip
    const fetchTripAlerts = async (trip: Trip) => {
        const tripKey = `${trip.tripStart}-${trip.tripEnd}`
        
        if (tripAlerts[tripKey]) {
            return // Already loaded
        }

        try {
            // Convert trip timestamps to seconds for comparison
            const tripStartSeconds = Math.floor(parseInt(String(trip.tripStart)) / 1000)
            const tripEndSeconds = Math.floor(parseInt(String(trip.tripEnd)) / 1000)

            const [dtcResponse, alarmResponse] = await Promise.all([
                getBoatDTCs({
                    deviceSerial: deviceId,
                    limit: 1000 // Get more records to filter
                }),
                getBoatAlarms({
                    deviceSerial: deviceId,
                    limit: 1000 // Get more records to filter
                })
            ])

            // Filter DTCs by trip time range
            let dtcCount = 0
            if (Array.isArray(dtcResponse)) {
                dtcCount = dtcResponse.filter((dtc: any) => {
                    if (dtc.dtc_timestamp) {
                        const dtcTime = new Date(dtc.dtc_timestamp).getTime() / 1000
                        return dtcTime >= tripStartSeconds && dtcTime <= tripEndSeconds
                    }
                    return false
                }).length
            }

            // Filter alarms by trip time range
            let alarmCount = 0
            if (Array.isArray(alarmResponse)) {
                alarmCount = alarmResponse.filter((alarm: any) => {
                    if (alarm.alarm_timestamp) {
                        const alarmTime = new Date(alarm.alarm_timestamp).getTime() / 1000
                        return alarmTime >= tripStartSeconds && alarmTime <= tripEndSeconds
                    }
                    return false
                }).length
            }

            setTripAlerts(prev => ({
                ...prev,
                [tripKey]: { dtcs: dtcCount, alarms: alarmCount }
            }))
        } catch (error) {
            console.error('Error fetching trip alerts:', error)
            setTripAlerts(prev => ({
                ...prev,
                [tripKey]: { dtcs: 0, alarms: 0 }
            }))
        }
    }

    // Calculate comprehensive trip metrics from GPS and telemetry data
    const calculateTripMetrics = (gpsData: any[], telemetryData: any) => {
        if (!gpsData || gpsData.length === 0) return null

        // Calculate distance traveled using Haversine formula
        const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number) => {
            const R = 3959 // Earth's radius in miles
            const dLat = (lat2 - lat1) * Math.PI / 180
            const dLon = (lon2 - lon1) * Math.PI / 180
            const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
                    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
                    Math.sin(dLon/2) * Math.sin(dLon/2)
            const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a))
            return R * c
        }

        let totalDistance = 0
        for (let i = 1; i < gpsData.length; i++) {
            const prev = gpsData[i-1]
            const curr = gpsData[i]
            const distance = calculateDistance(
                parseFloat(prev.latitude),
                parseFloat(prev.longitude),
                parseFloat(curr.latitude),
                parseFloat(curr.longitude)
            )
            totalDistance += distance
        }

        // Calculate metrics from telemetry data
        let avgSpeed = 0
        let maxSpeed = 0
        let fuelConsumption = 0
        let batteryVoltageChange = 0
        let engineTempMax = 0
        let engineTempAvg = 0
        let waterTempMax = 0
        let waterDepthMax = 0
        let ballastPortAvg = 0
        let ballastStbdAvg = 0
        let ballastBellyAvg = 0
        let profileUsed = 'Unknown'

        if (telemetryData) {
            // Speed metrics from GPS_Speed_mph
            if (telemetryData.GPS_Speed_mph && telemetryData.GPS_Speed_mph.length > 0) {
                const speeds = telemetryData.GPS_Speed_mph.map((point: any) => parseFloat(point.value) || 0)
                maxSpeed = Math.max(...speeds)
                avgSpeed = speeds.reduce((sum: number, speed: number) => sum + speed, 0) / speeds.length
            }

            // Fuel consumption from FUEL_LEVEL_LINC
            if (telemetryData.FUEL_LEVEL_LINC && telemetryData.FUEL_LEVEL_LINC.length > 0) {
                const fuelLevels = telemetryData.FUEL_LEVEL_LINC.map((point: any) => parseFloat(point.value) || 0)
                const startFuel = fuelLevels[0]
                const endFuel = fuelLevels[fuelLevels.length - 1]
                fuelConsumption = Math.max(0, startFuel - endFuel)
            }

            // Battery voltage change from Analog_Input_1, 2, 3
            if (telemetryData.Analog_Input_1 && telemetryData.Analog_Input_1.length > 0) {
                const voltages = telemetryData.Analog_Input_1.map((point: any) => parseFloat(point.value) || 0)
                const startVoltage = voltages[0]
                const endVoltage = voltages[voltages.length - 1]
                batteryVoltageChange = endVoltage - startVoltage
            }

            // Engine temperature
            if (telemetryData.Engine_Temp && telemetryData.Engine_Temp.length > 0) {
                const temps = telemetryData.Engine_Temp.map((point: any) => parseFloat(point.value) || 0)
                engineTempMax = Math.max(...temps)
                engineTempAvg = temps.reduce((sum: number, temp: number) => sum + temp, 0) / temps.length
            }

            // Water temperature
            if (telemetryData.WATER_TEMP && telemetryData.WATER_TEMP.length > 0) {
                const waterTemps = telemetryData.WATER_TEMP.map((point: any) => parseFloat(point.value) || 0)
                waterTempMax = Math.max(...waterTemps)
            }

            // Water depth
            if (telemetryData.WATER_DEPTH && telemetryData.WATER_DEPTH.length > 0) {
                const depths = telemetryData.WATER_DEPTH.map((point: any) => parseFloat(point.value) || 0)
                waterDepthMax = Math.max(...depths)
            }

            // Ballast levels
            if (telemetryData.BALLAST_PORT && telemetryData.BALLAST_PORT.length > 0) {
                const portLevels = telemetryData.BALLAST_PORT.map((point: any) => parseFloat(point.value) || 0)
                ballastPortAvg = portLevels.reduce((sum: number, level: number) => sum + level, 0) / portLevels.length
            }
            if (telemetryData.BALLAST_STBD && telemetryData.BALLAST_STBD.length > 0) {
                const stbdLevels = telemetryData.BALLAST_STBD.map((point: any) => parseFloat(point.value) || 0)
                ballastStbdAvg = stbdLevels.reduce((sum: number, level: number) => sum + level, 0) / stbdLevels.length
            }
            if (telemetryData.BALLAST_BELLY && telemetryData.BALLAST_BELLY.length > 0) {
                const bellyLevels = telemetryData.BALLAST_BELLY.map((point: any) => parseFloat(point.value) || 0)
                ballastBellyAvg = bellyLevels.reduce((sum: number, level: number) => sum + level, 0) / bellyLevels.length
            }

            // Profile used
            if (telemetryData.Profile && telemetryData.Profile.length > 0) {
                profileUsed = telemetryData.Profile[0].value || 'Unknown'
            }
        }

        return {
            totalDistance: Math.round(totalDistance * 100) / 100, // miles, 2 decimal places
            avgSpeed: Math.round(avgSpeed * 10) / 10, // mph, 1 decimal place
            maxSpeed: Math.round(maxSpeed * 10) / 10, // mph, 1 decimal place
            fuelConsumption: Math.round(fuelConsumption * 10) / 10, // percentage, 1 decimal place
            batteryVoltageChange: Math.round(batteryVoltageChange * 100) / 100, // volts, 2 decimal places
            engineTempMax: Math.round(engineTempMax * 10) / 10, // °F, 1 decimal place
            engineTempAvg: Math.round(engineTempAvg * 10) / 10, // °F, 1 decimal place
            waterTempMax: Math.round(waterTempMax * 10) / 10, // °F, 1 decimal place
            waterDepthMax: Math.round(waterDepthMax * 10) / 10, // feet, 1 decimal place
            ballastPortAvg: Math.round(ballastPortAvg * 10) / 10, // percentage, 1 decimal place
            ballastStbdAvg: Math.round(ballastStbdAvg * 10) / 10, // percentage, 1 decimal place
            ballastBellyAvg: Math.round(ballastBellyAvg * 10) / 10, // percentage, 1 decimal place
            profileUsed,
            pointCount: gpsData.length
        }
    }

    // Handle trip expansion
    const handleTripChange = (tripKey: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
        setExpandedTrip(isExpanded ? tripKey : false)

        if (isExpanded) {
            const trip = trips.find(t => `${t.tripStart}-${t.tripEnd}` === tripKey)
            if (trip) {
                fetchTripGPSData(trip)
                fetchTripTelemetryData(trip)
                fetchTripAlerts(trip)
            }
        }
    }

    // Effect to fetch location when GPS data becomes available
    useEffect(() => {
        Object.keys(tripGPSData).forEach(tripKey => {
            const gpsData = tripGPSData[tripKey]
            if (gpsData && gpsData.length > 0 && !tripLocations[tripKey]) {
                const trip = trips.find(t => `${t.tripStart}-${t.tripEnd}` === tripKey)
                if (trip) {
                    fetchTripLocation(trip, gpsData)
                }
            }
        })
    }, [tripGPSData, tripLocations, trips])

    useEffect(() => {
        if (deviceId) {
            fetchTrips()
        }
    }, [deviceId])

    // Preload GPS data for all trips to enable faster location loading
    useEffect(() => {
        if (trips.length > 0) {
            trips.forEach(trip => {
                const tripKey = `${trip.tripStart}-${trip.tripEnd}`
                if (!tripGPSData[tripKey] && !loadingGPS) {
                    // Fetch GPS data for location purposes (smaller limit for performance)
                    fetchTripGPSData(trip)
                }
            })
        }
    }, [trips])

    return (
        <ThemeProvider theme={theme}>
            <Box sx={{ width: '100%', bgcolor: '#1a1a1a' , minHeight: '50vh'}}>
                <Box sx={{ p: 3 }}>
                    <Paper sx={{ bgcolor: 'background.default', p: 2 }}>
                    {loading && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                            <CircularProgress size={24} />
                            <Typography variant="body2" sx={{ ml: 2, color: 'text.secondary' }}>
                                Loading trips...
                            </Typography>
                        </Box>
                    )}

                    {!deviceId && (
                        <Alert severity="info" sx={{ mt: 2 }}>
                            No device paired with this boat.
                        </Alert>
                    )}

                    {validTrips.length === 0 && !loading && deviceId && (
                        <Alert severity="info" sx={{ mt: 2 }}>
                            No valid trips found for this device.
                        </Alert>
                    )}

                    {currentTrips.map((trip) => {
                            const tripKey = `${trip.tripStart}-${trip.tripEnd}`
                            const tripComponents = formatTripComponents(trip.tripStart, trip.tripEnd)
                            const duration = formatDuration(trip.duration_seconds)
                            const gpsData = tripGPSData[tripKey] || []
                            const location = tripLocations[tripKey] || ''
                            const isLoadingGPS = loadingGPS === tripKey

                            return (
                                <Accordion
                                    key={tripKey}
                                    expanded={expandedTrip === tripKey}
                                    onChange={handleTripChange(tripKey)}
                                    sx={{
                                        bgcolor: 'background.paper',
                                        mb: 1,
                                        '&:before': { display: 'none' },
                                        '&.Mui-expanded': {
                                            margin: '0 0 8px 0',
                                        },
                                    }}
                                >
                                    <AccordionSummary
                                        expandIcon={<ExpandMoreIcon sx={{ color: 'primary.main' }} />}
                                        sx={{
                                            '& .MuiAccordionSummary-content': {
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                            },
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                                            {/* Left side - Date, Time, and Duration */}
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 600 }}>
                                                    {tripComponents.date}
                                                </Typography>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Typography variant="body1" sx={{ color: 'text.primary', fontWeight: 500}}>
                                                        {tripComponents.startTime}
                                                    </Typography>
                                                    <Typography variant="body1" sx={{ color: 'text.secondary', mx: 0.5 }}>
                                                        →
                                                    </Typography>
                                                    <Typography variant="body1" sx={{ color: 'text.primary', fontWeight: 500}}>
                                                        {tripComponents.endTime}
                                                    </Typography>
                                                </Box>
                                                <Chip
                                                    label={duration}
                                                    color="primary"
                                                    variant="outlined"
                                                    size="small"
                                                />
                                            </Box>

                                            {/* Right side - Location */}
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                                                {location ? (
                                                    <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic', textAlign: 'right' }}>
                                                        {location}
                                                    </Typography>
                                                ) : (
                                                    <Typography variant="body2" sx={{ color: 'text.disabled', fontStyle: 'italic', textAlign: 'right' }}>
                                                        Loading location...
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Box>
                                    </AccordionSummary>

                                    <AccordionDetails sx={{ pt: 0 }}>
                                        <Divider sx={{ mb: 2 }} />

                                        {isLoadingGPS && (
                                            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                                                <CircularProgress size={20} />
                                                <Typography variant="body2" sx={{ ml: 2, color: 'text.secondary' }}>
                                                    Loading GPS data...
                                                </Typography>
                                            </Box>
                                        )}

                                        {gpsData.length > 0 && (() => {
                                            const telemetryData = tripTelemetryData[tripKey] || {}
                                            const metrics = calculateTripMetrics(gpsData, telemetryData)
                                            const isLoadingTelemetry = loadingTelemetry?.[tripKey] || false

                                            return (
                                                <>
                                                    {/* Split Layout: Map on left, Metrics on right */}
                                                    <Box sx={{ display: 'flex', gap: 3, height: '400px' }}>
                                                        {/* Left Half - Trip Route Map */}
                                                        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                                                            <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.primary' }}>
                                                                Trip Route
                                                            </Typography>
                                                            <Box sx={{ flex: 1, borderRadius: 1, overflow: 'hidden' }}>
                                                                <TripMap gpsData={gpsData} />
                                                            </Box>
                                                        </Box>

                                                        {/* Right Half - Trip Metrics */}
                                                        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                                                            <Typography variant="subtitle2" sx={{ color: 'text.primary' }}>
                                                                Trip Metrics
                                                            </Typography>
                                                            
                                                            {/* Trip Summary Cards */}
                                                            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2, flex: 1 }}>
                                                                <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
                                                                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                                                        Distance Traveled
                                                                    </Typography>
                                                                    {/* <Typography variant="h6" color="primary">
                                                                        {metrics?.totalDistance} miles
                                                                    </Typography> */}
                                                                    <Typography variant="h6" color="primary">
                                                                        {(metrics?.totalDistance != null ? (metrics.totalDistance / 10).toFixed(1) : 'N/A')} miles
                                                                    </Typography>
                                                                </Paper>
                                                                <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
                                                                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                                                        Average Speed
                                                                    </Typography>
                                                                    <Typography variant="h6" color="info.main">
                                                                        {metrics?.avgSpeed} mph
                                                                    </Typography>
                                                                </Paper>
                                                                <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
                                                                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                                                        Max Speed
                                                                    </Typography>
                                                                    <Typography variant="h6" color="warning.main">
                                                                        {metrics?.maxSpeed} mph
                                                                    </Typography>
                                                                </Paper>
                                                                <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
                                                                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                                                        Fuel Used
                                                                    </Typography>
                                                                    <Typography variant="h6" color="error.main">
                                                                        {metrics?.fuelConsumption}%
                                                                    </Typography>
                                                                </Paper>
                                                                <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
                                                                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                                                        Battery Change
                                                                    </Typography>
                                                                    <Typography variant="h6" color={(metrics?.batteryVoltageChange ?? 0) >= 0 ? "success.main" : "error.main"}>
                                                                        {(metrics?.batteryVoltageChange ?? 0) >= 0 ? '+' : ''}{metrics?.batteryVoltageChange ?? 0}V
                                                                    </Typography>
                                                                </Paper>
                                                                <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
                                                                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                                                        Engine Temp Max
                                                                    </Typography>
                                                                    <Typography variant="h6" color="warning.main">
                                                                        {metrics?.engineTempMax}°F
                                                                    </Typography>
                                                                </Paper>
                                                            </Box>

                                                            {/* Ballast Configuration */}
                                                            {metrics && ((metrics.ballastPortAvg ?? 0) > 0 || (metrics.ballastStbdAvg ?? 0) > 0 || (metrics.ballastBellyAvg ?? 0) > 0) && (
                                                                <Box>
                                                                    <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.primary' }}>
                                                                        Ballast Configuration
                                                                    </Typography>
                                                                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                                                        {(metrics.ballastPortAvg ?? 0) > 0 && (
                                                                            <Chip
                                                                                label={`Port: ${metrics.ballastPortAvg}%`}
                                                                                color="primary"
                                                                                variant="outlined"
                                                                                size="small"
                                                                            />
                                                                        )}
                                                                        {(metrics.ballastStbdAvg ?? 0) > 0 && (
                                                                            <Chip
                                                                                label={`Starboard: ${metrics.ballastStbdAvg}%`}
                                                                                color="secondary"
                                                                                variant="outlined"
                                                                                size="small"
                                                                            />
                                                                        )}
                                                                        {(metrics.ballastBellyAvg ?? 0) > 0 && (
                                                                            <Chip
                                                                                label={`Belly: ${metrics.ballastBellyAvg}%`}
                                                                                color="info"
                                                                                variant="outlined"
                                                                                size="small"
                                                                            />
                                                                        )}
                                                                    </Box>
                                                                </Box>
                                                            )}

                                                            {/* Loading indicator for telemetry */}
                                                            {isLoadingTelemetry && (
                                                                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2 }}>
                                                                    <CircularProgress size={20} />
                                                                    <Typography variant="body2" color="text.secondary">
                                                                        Loading telemetry data...
                                                                    </Typography>
                                                                </Box>
                                                            )}
                                                        </Box>
                                                    </Box>
                                                </>
                                            )
                                        })()}

                                        {gpsData.length === 0 && !isLoadingGPS && (
                                            <Alert severity="info">
                                                No GPS data available for this trip.
                                            </Alert>
                                        )}
                                    </AccordionDetails>
                                </Accordion>
                            )
                        })}

                        {/* Combined Trip Summary and Pagination */}
                        {validTrips.length > 0 && (
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, mb: 1 }}>
                                <Typography variant="body2" color="text.secondary">
                                    Showing {startIndex + 1}-{Math.min(endIndex, validTrips.length)} of {validTrips.length} trips
                                </Typography>
                                {totalPages > 1 && (
                                    <Typography variant="body2" color="text.secondary">
                                        Page {currentPage} of {totalPages}
                                    </Typography>
                                )}
                            </Box>
                        )}

                        {/* Pagination */}
                        {totalPages > 1 && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', mt: -4, mb: 0 }}>
                                <Pagination
                                    count={totalPages}
                                    page={currentPage}
                                    onChange={handlePageChange}
                                    color="primary"
                                    size="medium"
                                    showFirstButton
                                    showLastButton
                                    sx={{
                                        '& .MuiPaginationItem-root': {
                                            color: 'text.primary',
                                            '&.Mui-selected': {
                                                backgroundColor: 'primary.main',
                                                color: 'white',
                                                '&:hover': {
                                                    backgroundColor: 'primary.dark',
                                                },
                                            },
                                            '&:hover': {
                                                backgroundColor: 'action.hover',
                                            },
                                        },
                                    }}
                                />
                            </Box>
                        )}
                    </Paper>
                </Box>
            </Box>
        </ThemeProvider>
    )
}

export default TripsView
